import { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { getConsent, subscribeConsent } from "@/lib/consent";

// GA4 (gtag.js) with SPA-aware page_view tracking.
// Gated behind analytics-cookie consent (UK GDPR / PECR).
const GA_ID = process.env.REACT_APP_GA4_ID;

const Analytics = () => {
  const { pathname, search } = useLocation();
  const loaded = useRef(false);
  const [analyticsAllowed, setAnalyticsAllowed] = useState(false);

  // Track consent state — start false, read localStorage after mount to avoid
  // hydration mismatch, then subscribe to future changes from the banner.
  useEffect(() => {
    const c = getConsent();
    if (c && c.analytics) setAnalyticsAllowed(true);
    return subscribeConsent((next) => setAnalyticsAllowed(Boolean(next.analytics)));
  }, []);

  // Load gtag.js exactly once, once consent granted.
  useEffect(() => {
    if (!GA_ID || loaded.current || !analyticsAllowed) return;
    loaded.current = true;

    const s = document.createElement("script");
    s.async = true;
    s.src = `https://www.googletagmanager.com/gtag/js?id=${GA_ID}`;
    document.head.appendChild(s);

    window.dataLayer = window.dataLayer || [];
    window.gtag = function () { window.dataLayer.push(arguments); };
    window.gtag("js", new Date());
    // Disable auto page_view — we fire manually on route changes for SPA accuracy
    window.gtag("config", GA_ID, { send_page_view: false });
  }, [analyticsAllowed]);

  // Fire page_view on every route change — only if gtag is loaded (which
  // implies consent was granted).
  useEffect(() => {
    if (!analyticsAllowed || !GA_ID || typeof window.gtag !== "function") return;
    const page_path = pathname + (search || "");
    window.gtag("event", "page_view", {
      page_path,
      page_location: window.location.href,
      page_title: document.title,
    });
  }, [pathname, search, analyticsAllowed]);

  return null;
};

export default Analytics;
